import React, {useEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import PhonelinkLockIcon from "@material-ui/icons/PhonelinkLock";
import {QRCodeSVG} from "qrcode.react";
import {useSelector} from "react-redux";
import {useTotp} from "store/two-factor/totp.store";
import {selectUser} from "store/auth/selectors";
import {useShallow} from "zustand/react/shallow";
import {formatISODate} from "tools/formats/formatDate";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import ConfirmPasswordModal from "view/components/ConfirmPasswordModal";
import Alert from "view/components/Alert";
import CodeInput from "view/components/CodeInput";
import RecoveryCodes from "view/components/RecoveryCodes";
import useStyles from "./styles";

type Props = {
  onBack: () => void;
};

const AuthenticatorManager: React.FC<Props> = ({onBack}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const locale = useDateFnsLocale();

  const {
    status,
    setup,
    recoveryCodes,
    fetchStatus,
    beginSetup,
    verifySetup,
    revoke,
    clearSetup,
    clearRecoveryCodes,
  } = useTotp(
    useShallow((state) => ({
      status: state.status,
      setup: state.setup,
      recoveryCodes: state.recoveryCodes,
      fetchStatus: state.fetchStatus,
      beginSetup: state.beginSetup,
      verifySetup: state.verifySetup,
      revoke: state.revoke,
      clearSetup: state.clearSetup,
      clearRecoveryCodes: state.clearRecoveryCodes,
    }))
  );

  const {hasPassword} = useSelector(selectUser);

  const [isConfirmingSetup, setIsConfirmingSetup] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  useEffect(() => {
    fetchStatus();
    return () => {
      clearSetup();
      clearRecoveryCodes();
    };
  }, [fetchStatus, clearSetup, clearRecoveryCodes]);

  const handleConfirmSetup = async (password?: string) => {
    await beginSetup(password);
    setIsConfirmingSetup(false);
    setCode("");
    setVerifyError(null);
  };

  const handleVerify = async (submittedCode: string = code, silent = false) => {
    setVerifyError(null);
    setIsVerifying(true);
    try {
      await verifySetup(submittedCode);
      setJustAdded(true);
    } catch (e) {
      if (silent) return;
      const status = (e as {response?: {status?: number}})?.response?.status;
      setVerifyError(
        status === 429
          ? t("common.two_factor.too_many_attempts")
          : t("common.two_factor.incorrect_code")
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleConfirmRevoke = async (password?: string) => {
    await revoke(password);
    setIsRevoking(false);
  };

  const renderSetup = () => {
    if (!setup) return null;

    return (
      <div className={classes.setup}>
        <div className={classes.qrBox}>
          <QRCodeSVG value={setup.otpauthUri} size={176} />
        </div>
        <div className={classes.secretRow}>
          <Typography className={classes.secretLabel}>
            <T keyName="common.two_factor.authenticator.manual_key" />
          </Typography>
          <Typography className={classes.secretValue}>
            {setup.secret}
          </Typography>
        </div>
        <Typography className={classes.helper}>
          <T keyName="common.two_factor.authenticator.enter_code" />
        </Typography>
        <CodeInput
          value={code}
          onChange={setCode}
          disabled={isVerifying}
          autoFocus
          onComplete={handleVerify}
        />
        {verifyError && (
          <div className={classes.alertWrapper}>
            <Alert severity="error" fullWidth>
              {verifyError}
            </Alert>
          </div>
        )}
        <div className={classes.actionsRow}>
          <Button
            color="primary"
            disabled={isVerifying}
            onClick={() => {
              clearSetup();
              setCode("");
              setVerifyError(null);
            }}
          >
            <T keyName="common.action.cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isVerifying || code.length < 6}
            endIcon={
              isVerifying ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            onClick={() => handleVerify()}
          >
            <T keyName="common.action.verify" />
          </Button>
        </div>
      </div>
    );
  };

  const renderManage = () => (
    <>
      {status?.configured ? (
        <div className={classes.rowCard}>
          <div className={classes.iconBox}>
            <PhonelinkLockIcon />
          </div>
          <div className={classes.rowContent}>
            <Typography className={classes.rowTitle}>
              <T keyName="common.two_factor.method.authenticator" />
            </Typography>
            <Typography className={classes.rowSubtitle}>
              {status.verifiedAt
                ? t("common.two_factor.enabled_on", {
                    date: formatISODate(
                      status.verifiedAt,
                      "d MMMM yyyy",
                      locale
                    ),
                  })
                : t("common.two_factor.enabled")}
            </Typography>
          </div>
          <IconButton
            size="small"
            onClick={() => {
              if (hasPassword) {
                setIsRevoking(true);
              } else {
                handleConfirmRevoke();
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      ) : (
        status !== null && (
          <Typography variant="body2" className={classes.emptyText}>
            <T keyName="common.two_factor.authenticator.description" />
          </Typography>
        )
      )}
      <div className={classes.buttonWrapper}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            if (hasPassword) {
              setIsConfirmingSetup(true);
            } else {
              handleConfirmSetup();
            }
          }}
        >
          {status?.configured
            ? t("common.two_factor.authenticator.change")
            : t("common.two_factor.authenticator.setup")}
        </Button>
      </div>
    </>
  );

  return (
    <>
      <div className={classes.backRow}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography className={classes.backTitle}>
          {recoveryCodes ? (
            <T keyName="common.two_factor.recovery.save_title" />
          ) : (
            <T keyName="common.two_factor.method.authenticator" />
          )}
        </Typography>
      </div>

      {justAdded && (
        <div
          className={
            recoveryCodes
              ? `${classes.alertWrapper} ${classes.alertInset}`
              : classes.alertWrapper
          }
        >
          <Alert severity="success" fullWidth>
            Authenticator app added as a 2-Step Verification method
          </Alert>
        </div>
      )}

      {recoveryCodes ? (
        <RecoveryCodes codes={recoveryCodes} onDone={clearRecoveryCodes} />
      ) : setup ? (
        renderSetup()
      ) : (
        renderManage()
      )}

      {isConfirmingSetup && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.authenticator")}
          description={t("common.two_factor.authenticator.setup_prompt")}
          submitTitle={t("common.action.continue")}
          onClose={() => setIsConfirmingSetup(false)}
          onConfirm={handleConfirmSetup}
        />
      )}
      {isRevoking && (
        <ConfirmPasswordModal
          subtitle={t("common.two_factor.method.authenticator")}
          description={t("common.two_factor.authenticator.remove_prompt")}
          submitTitle={t("common.two_factor.revoke")}
          onClose={() => setIsRevoking(false)}
          onConfirm={handleConfirmRevoke}
        />
      )}
    </>
  );
};

export default AuthenticatorManager;
